import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

import LoginTemplate from './components/LoginTemplate/LoginTemplate';
import LoginIcon from '@mui/icons-material/Login';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PinOutlinedIcon from '@mui/icons-material/PinOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import {
    Box,
    Card,
    TextField,
    InputAdornment,
    IconButton,
    Typography,
    Button,
    CircularProgress,
    Alert,
} from '@mui/material';

import URL2 from './config.js';
import CustomSnackbar from './components/common/snackbar/CustomSnackbar.jsx';
import { useDispatch } from 'react-redux';
import { clearAllPersistedData } from './utils/persistUtils';
import { loginSuccess } from './features/auth/login.js';
import { reEnrolment_status } from './utils/constant.js';
import { reenrolmentYear } from './utils/years.js';
import getUserIP from './services/getIPAddress.js';
import checkStatus from './jsFunctions/checkStatus.js';
import {
    setCampusPaymentOwner,
    storeCampusSessions,
} from './utils/campusPaymentCharges.js';
import { resolveCampusPaymentResume } from './utils/paymentResume.js';
import { hasOutstandingCampusPayment } from './utils/campusPaymentStatus.js';

const validationSchema = Yup.object({
    ParentID: Yup.string().required('Parent ID is required'),
    ParPassword: Yup.string().required('Password is required'),
});

const ARLogin = () => {
    const [snackBarData, setSnackBarData] = useState({
        is_enabled: false,
        message: '',
        variant: '',
    });

    const [ParIDError, setParIDError] = useState(false);
    const [ProhibitedError, setProhibitedError] = useState(false);
    const [ProhibitedMessage, setProhibitedMessage] = useState('');
    const [PassError, setPassError] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [statusData, setStatusData] = useState(null);
    const [isLoadingStatus, setIsLoadingStatus] = useState(true);
    const [isLoadingLogin, setIsLoadingLogin] = useState(false);

    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        const loadInitialData = async () => {
            const logID = localStorage.getItem('LogID');
            if (logID && logID !== 'NA' && logID !== 'null') {
                await fetchStatusData();
            } else {
                setIsLoadingStatus(false);
            }
        };
        loadInitialData();
    }, []);

    const fetchStatusData = async () => {
        try {
            setIsLoadingStatus(true);
            const result = await checkStatus();

            if (result && result.t_date && result.t_time) {
                setStatusData(result);
            }
            return result;
        } catch (err) {
            console.error("Error fetching status:", err);
            return null;
        } finally {
            setIsLoadingStatus(false);
        }
    };

    const fetchUserIP = async () => {
        try {
            const userIP = await getUserIP();
            return userIP;
        } catch (err) {
            console.error("Error fetching user IP:", err);
            return null;
        }
    };

    const CredentialsMatch = async (values) => {
        const { ParentID, ParPassword } = values;
        const arLogin = true;
        const isGhostLogin = false;

        setSnackBarData({
            is_enabled: false,
            message: '',
            variant: '',
        });
        setParIDError(false);
        setProhibitedError(false);
        setProhibitedMessage('');
        setPassError(false);
        setIsLoadingLogin(true);

        // Whoever was signed in on this browser before is signed out now, before the request: a
        // login that fails must not leave the previous family's token and answers behind.
        clearAllPersistedData(dispatch);

        try {
            const { data } = await URL2.post("/parent-login", {
                u_ParID: ParentID,
                u_ParPass: ParPassword,
                arLogin,
            });

            if (data?.is_enabled === false) {
                setSnackBarData({
                    is_enabled: true,
                    message: data.message,
                    variant: 'error',
                });
                return;
            }

            if (data === "Parent ID does not exists") {
                return setSnackBarData({
                    is_enabled: true,
                    message: data,
                    variant: 'error',
                });
            }

            // The plain string is what the server used to send and may still; the object carries the
            // school's own message from Settings. Either way the family is turned away — and if a
            // newer server ever sends a shape neither of these matches, the LoginToken check below
            // refuses the sign-in rather than letting a blocked family through.
            if (data === "Student Prohibited" || data?.prohibited === true) {
                setProhibitedMessage(typeof data?.message === 'string' ? data.message : '');
                return setProhibitedError(true);
            }

            // Only a real token is stored: the inner pages let in anything that is there.
            if (typeof data?.LoginToken !== 'string' || !data.LoginToken) {
                return setSnackBarData({
                    is_enabled: true,
                    message: 'Could not sign in. Please try again.',
                    variant: 'error',
                });
            }

            localStorage.setItem('LoginToken', data.LoginToken);
            setCampusPaymentOwner(ParentID);

            let latestStatus = null;
            if (data.data_log_id) {
                localStorage.setItem('LogID', data.data_log_id);
                if (Array.isArray(data.campusStatuses) && data.campusStatuses.length) {
                    storeCampusSessions(data.campusStatuses.map((c) => ({
                        SessionID: c.SessionID,
                        college: c.college,
                        parent_code: c.parent_code,
                    })));
                }
                latestStatus = await fetchStatusData();
            }

            const navState = { ParentID: ParentID, isGhostLogin, credit_amount: parseFloat(Math.abs(data.credit_amount)), college: data.college };
            localStorage.setItem('navState', JSON.stringify(navState));

            if (data.PasswordMatched === "Yes") {
                const familyStatus = { ...data, ...(latestStatus || {}) };
                const userIP = await fetchUserIP();

                const completeLogin = () => dispatch(loginSuccess({
                    LoginToken: data.LoginToken,
                    ParentID: values.ParentID,
                    userIP,
                    college: data.college,
                    SessionID: data.data_log_id || data.SessionID,
                }));

                if (reEnrolment_status.includes(familyStatus.RenrolStatus)) {
                    completeLogin();
                    return navigate('/reenrolmentcompleted', {
                        state: {
                            t_date: latestStatus?.t_date || statusData?.t_date || familyStatus.t_date || null,
                            t_time: latestStatus?.t_time || statusData?.t_time || familyStatus.t_time || null,
                            campusStatuses: familyStatus.campusStatuses || [],
                            ParentID,
                            college: data.college,
                        },
                    });
                }

                const resumePaymentState = resolveCampusPaymentResume(familyStatus, {
                    ParentID,
                    SessionID: data.data_log_id,
                    college: data.college,
                    credit_amount: navState.credit_amount,
                });

                if (resumePaymentState) {
                    completeLogin();
                    return navigate('/payment', { state: resumePaymentState });
                }

                const outstandingCampusPayment = hasOutstandingCampusPayment(familyStatus);

                dispatch(loginSuccess({
                    LoginToken: data.LoginToken,
                    ParentID: values.ParentID,
                    userIP,
                    college: data.college,
                    SessionID: data.SessionID
                }));

                return navigate(`/parentinformation`, {
                    state: { ...navState, resumePayment: outstandingCampusPayment },
                });
            }

            const isTestMode = String(process.env.REACT_APP_TEST_MODE).toLowerCase() === 'true';

            if (!isTestMode && data.PasswordMatched === "No") {
                setSnackBarData({
                    is_enabled: true,
                    message: "Invalid Parent Code or Password",
                    variant: 'error',
                });
                return;
            }
            else {
                dispatch(loginSuccess({
                    LoginToken: data.LoginToken,
                    ParentID: values.ParentID,
                    userIP: await fetchUserIP(),
                    college: data.college,
                    SessionID: data.SessionID || data.data_log_id
                }));

                return navigate(`/parentinformation`, { state: navState });
            }
        }
        catch (error) {
            const errorData = error?.response?.data;
            const errorMessage =
                (typeof errorData?.error?.error === 'string' && errorData.error.error) ||
                (typeof errorData?.error === 'string' && errorData.error) ||
                (typeof errorData === 'string' && errorData) ||
                "An unexpected error occurred";

            setSnackBarData({
                is_enabled: true,
                message: errorMessage,
                variant: 'error',
            });
        }
        finally {
            setIsLoadingLogin(false);
        }
    };

    const content = (
        <Card
            elevation={0}
            sx={{
                width: '100%',
                maxWidth: '460px',
                borderRadius: '24px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.02)',
                bgcolor: '#ffffff',
                p: { xs: 3, sm: 4.5 },
                overflow: 'hidden',
            }}
        >
            <Box sx={{ textAlign: 'center', mb: 3.5 }}>
                <Box
                    component="img"
                    src={`${process.env.PUBLIC_URL}/assets/logo.png`}
                    alt="Australian Islamic College Logo"
                    sx={{
                        maxHeight: '76px',
                        maxWidth: '100%',
                        height: 'auto',
                        objectFit: 'contain',
                        mx: 'auto',
                        mb: 2,
                    }}
                />
                <Typography
                    variant="h5"
                    component="h1"
                    sx={{
                        fontWeight: 700,
                        color: '#1e293b',
                        fontSize: { xs: '20px', sm: '23px' },
                        letterSpacing: '-0.02em',
                    }}
                >
                    Accounts Receivable Login
                </Typography>
                <Typography
                    sx={{
                        color: '#64748b',
                        fontSize: '14px',
                        mt: 0.75,
                    }}
                >
                    Sign in with Parent credentials
                </Typography>
            </Box>

            {snackBarData.is_enabled && (
                <CustomSnackbar
                    message={snackBarData.message}
                    variant={snackBarData.variant}
                />
            )}

            {ProhibitedError && (
                <Alert
                    severity="warning"
                    sx={{
                        mb: 2.5,
                        borderRadius: '12px',
                        fontSize: '13.5px',
                        textAlign: 'left',
                    }}
                >
                    {/* The school's wording, from Settings. This was hard-coded here, phone number
                        and all, so a change of officer or number meant a deploy. The fallback is
                        the old text exactly, for a server that has not been updated yet — never a
                        blank alert, which tells a blocked family nothing at all. */}
                    {ProhibitedMessage || (
                        <>
                            Your child's {reenrolmentYear()} Re-enrolment has been blocked. Please contact the Enrolment Officer at <strong>(08) 9362 2100</strong>.
                        </>
                    )}
                </Alert>
            )}

            <Formik
                initialValues={{ ParentID: '', ParPassword: '' }}
                validationSchema={validationSchema}
                onSubmit={CredentialsMatch}
            >
                {({ errors, touched, handleSubmit, setFieldValue }) => {
                    return (
                        <Form onSubmit={handleSubmit}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                <Field name="ParentID">
                                    {() => (
                                        <TextField
                                            label="Parent ID"
                                            placeholder="Enter Parent ID"
                                            fullWidth
                                            size="medium"
                                            variant="outlined"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <PinOutlinedIcon sx={{ color: '#64748b' }} fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '12px',
                                                    bgcolor: '#ffffff',
                                                    fontSize: '15px',
                                                    transition: 'all 0.2s ease',
                                                    '& fieldset': {
                                                        borderColor: '#cbd5e1',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: '#94a3b8',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#059669',
                                                        borderWidth: '2px',
                                                    },
                                                },
                                                '& .MuiInputLabel-root.Mui-focused': {
                                                    color: '#059669',
                                                },
                                                '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                                                    display: 'none',
                                                },
                                                '& input[type=number]': {
                                                    MozAppearance: 'textfield',
                                                },
                                            }}
                                            error={Boolean(errors.ParentID && touched.ParentID) || ParIDError}
                                            helperText={(touched.ParentID && errors.ParentID) || (ParIDError && 'Parent ID does not match')}
                                            onChange={(e) => {
                                                setFieldValue('ParentID', e.target.value);
                                                setParIDError(false);
                                            }}
                                            type="number"
                                        />
                                    )}
                                </Field>

                                <Field name="ParPassword">
                                    {() => (
                                        <TextField
                                            label="Password"
                                            placeholder="Enter password"
                                            type={showPassword ? 'text' : 'password'}
                                            fullWidth
                                            size="medium"
                                            variant="outlined"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <LockOutlinedIcon sx={{ color: '#64748b' }} fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label="toggle password visibility"
                                                            onClick={() => setShowPassword(!showPassword)}
                                                            edge="end"
                                                            size="small"
                                                            sx={{ color: '#64748b' }}
                                                        >
                                                            {showPassword ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '12px',
                                                    bgcolor: '#ffffff',
                                                    fontSize: '15px',
                                                    transition: 'all 0.2s ease',
                                                    '& fieldset': {
                                                        borderColor: '#cbd5e1',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: '#94a3b8',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#059669',
                                                        borderWidth: '2px',
                                                    },
                                                },
                                                '& .MuiInputLabel-root.Mui-focused': {
                                                    color: '#059669',
                                                },
                                            }}
                                            error={Boolean(errors.ParPassword && touched.ParPassword) || PassError}
                                            helperText={(touched.ParPassword && errors.ParPassword) || (PassError && 'Invalid Parent Code or Password')}
                                            onChange={(e) => {
                                                setFieldValue('ParPassword', e.target.value);
                                                setPassError(false);
                                            }}
                                        />
                                    )}
                                </Field>

                                <Button
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    type="submit"
                                    disabled={isLoadingLogin}
                                    endIcon={!isLoadingLogin ? <LoginIcon /> : null}
                                    sx={{
                                        mt: 0.5,
                                        bgcolor: '#059669',
                                        color: '#FFFFFF',
                                        fontWeight: 700,
                                        py: 1.5,
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontSize: '16px',
                                        minHeight: '48px',
                                        boxShadow: '0 4px 14px rgba(5, 150, 105, 0.35)',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            bgcolor: '#047857',
                                            boxShadow: '0 6px 20px rgba(5, 150, 105, 0.45)',
                                            transform: 'translateY(-1px)',
                                        },
                                        '&:active': {
                                            transform: 'translateY(0)',
                                        },
                                        '&.Mui-disabled': {
                                            bgcolor: '#059669',
                                            opacity: 0.7,
                                            color: '#FFFFFF',
                                        },
                                    }}
                                >
                                    {isLoadingLogin ? <CircularProgress size={22} sx={{ color: '#FFFFFF' }} /> : 'Sign In'}
                                </Button>
                            </Box>
                        </Form>
                    );
                }}
            </Formik>
        </Card>
    );

    return <LoginTemplate content={content} />;
};

export default ARLogin;